import React, { useEffect, useRef, useState } from 'react';
import Digit from './Digit';
import FixedChar from './FixedChar';
import styles from './Roller.module.scss';
import VirtualCounterManager from '../api/VirtualCounterManager';

/**
 * The spacing appearance of a chunk of the counter
 */
type ChunkSize = 'monospaced' | 'normal' | 'micro';

interface IRollerProps {
    /**
     * The label which appears above the counter
     */
    label?: string;
    /**
     * Endpoint URL
     */
    url: string;
    /**
     * When using a graphic source for characters,
     * the path to said resource
     */
    charSrc?: string;
    /**
     * Roller background css override
     */
    backgroundCss?: string;
    /**
     * The width of groups chars
     * the comma in $1,000.00
     */
    groupSize?: ChunkSize;
    /**
     * The direction to merge if micro
     */
    groupMerge?: string | 'left' | 'right';
    /**
     * The width of decimal chars
     * the period in $1,000.00
     */
    decimalSize?: ChunkSize;
    /**
     * The direction to merge if micro
     */
    decimalMerge?: string | 'left' | 'right';
    /**
     * The width of literal chars
     * commonly the space before the dollar sign in CAD's 1 000,00 $
     */
    literalSize?: ChunkSize;
    /**
     * The width of currency chars
     * the dollar or euro in $1,000.00 or 1.000,00 €, respectively
     */
    currencySize?: ChunkSize;
    /**
     * The language the currency is formatted for
     */
    lang?: string;
    /**
     * The currency the input number is being formatted into
     */
    currency?: string;
    /**
     * Whether to use grouping separators on the displayed value
     * For example in USD $1000000 vs $1,000,000 where the comma
     * groups three digits.
     */
    useGrouping?: boolean;
}

const Roller: React.FC<IRollerProps> = ({
    url,
    charSrc,
    backgroundCss,
    groupSize = 'micro',
    groupMerge = '',
    decimalSize = 'micro',
    decimalMerge = '',
    literalSize = 'normal',
    currencySize = 'monospaced',
    lang = 'en-US',
    currency = 'USD',
    useGrouping = true,
}) => {
    const [rollerValueInCents, setRollerValueInCents] = useState<number>(0);
    const [rollerChars, setRollerChars] = useState<Intl.NumberFormatPart[]>([]);
    const [animated, setAnimated] = useState(false);
    const virtualCounter = VirtualCounterManager.getCounter(url);

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const rollerRef = useRef<HTMLDivElement | null>(null);
    const formatter = new Intl.NumberFormat(lang, {
        style: 'currency',
        currency,
        useGrouping,
    });

    useEffect(() => {
        let animationID: undefined | number;
        const updateJackpot = () => {
            animationID = requestAnimationFrame(updateJackpot);

            // Skip if date is the same
            if (rollerValueInCents === virtualCounter.current.current) return;

            setRollerValueInCents(virtualCounter.current.current);
            setAnimated(virtualCounter.animated);

            const parts = formatter.formatToParts(virtualCounter.current.current / 100);
            setRollerChars(parts);
        };

        updateJackpot();

        return () => {
            if (animationID) {
                cancelAnimationFrame(animationID);
            }
        };
    });

    // dev code to change the digit count on click
    // const bump = () => {
    //     const counter = document.querySelector<HTMLDivElement>('[data-character-count]')!;
    //     const choices = [1, 12, 123, 1234, 12345, 123456, 1234567, 12345678, 1234567890];
    //     const choice = choices[Number(counter.dataset.characterCount) % choices.length];
    //     const parts = formatter.formatToParts(choice / 100);
    //     setRollerChars(parts);
    //     counter.dataset.characterCount = choice.toFixed().length.toString();
    // };

    // useEffect(() => {
    //     document.addEventListener('click', bump);
    //     return () => {
    //         document.removeEventListener('click', bump);
    //     };
    // });

    // useEffect(() => {
    //     const formatted = new Intl.NumberFormat(lang, {
    //         style: 'currency',
    //         currency,
    //         useGrouping,
    //     });
    //     const parts = formatted.formatToParts(counterValue);
    //     setCounterChars(parts);

    //     const interval = setInterval(() => {
    //         setCounterValue(counterValue + Math.random());
    //     }, 250);

    //     return () => clearInterval(interval);
    // }, [counterValue, lang, currency, useGrouping]);

    const getInclusions = (
        arr: Intl.NumberFormatPart[],
        index: number,
        innerArr: string[],
        innerIndex: number
    ): (Intl.NumberFormatPart & { position: 'right' | 'left' })[] => {
        const before = arr[index - 1];
        const after = arr[index + 1];

        const isMicroAndMinor = (chunk: Intl.NumberFormatPart) => {
            if (!chunk) return false;
            return (chunk.type === 'group' && groupSize === 'micro') || (chunk.type === 'decimal' && decimalSize === 'micro');
        };

        const isInReleventPosition = (position: 'before' | 'after', chunk: Intl.NumberFormatPart) => {
            // TODO:
            /**
             * tackle this from top down aka look at the full number,
             * find the inclusions, then insert them. 123,456.78, find
             * the ordinal pos of each special char and plan to insert
             * vs going through each chunk
             */
            if (!chunk) return false;
            if (position === 'before') {
                return (groupMerge === 'right' && chunk.type === 'group') || (decimalMerge === 'right' && chunk.type === 'decimal');
            } else {
                // else 'after'
                return (groupMerge === 'left' && chunk.type === 'group') || (decimalMerge === 'left' && chunk.type === 'decimal');
            }
        };

        const inclusions: (Intl.NumberFormatPart & {
            position: 'right' | 'left';
        })[] = [];

        /**
         * determine the inner digits position. so that if the number chunk
         * is 197,189..the 7 can be found split out and given the comma
         */
        const isLastDigit = innerArr.length - 1 === innerIndex;
        const isFirstDigit = innerIndex === 0;

        if (isFirstDigit && isMicroAndMinor(before) && isInReleventPosition('before', before)) {
            inclusions.push({
                ...before,
                position: 'left',
            });
        }
        if (isLastDigit && isMicroAndMinor(after) && isInReleventPosition('after', after)) {
            inclusions.push({
                ...after,
                position: 'right',
            });
        }

        return inclusions;
    };

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <div
                className={styles.roller}
                style={{
                    background: backgroundCss,
                    // transform: containerScale,
                    // height: containerHeight,
                }}
                ref={rollerRef}>
                {rollerChars.map((chunk, index, arr) => {
                    if (chunk.type === 'integer' || chunk.type === 'fraction') {
                        return chunk.value
                            .split('')
                            .map((char, indexA, arrA) => (
                                <Digit
                                    animated={animated}
                                    key={`${index}-${indexA}`}
                                    charSrc={charSrc}
                                    currentNumber={Number(char)}
                                    inclusions={getInclusions(arr, index, arrA, indexA)}
                                />
                            ));
                    } else {
                        let size;
                        let merge;
                        switch (chunk.type) {
                            case 'group':
                                if (groupSize === 'micro') return;
                                size = groupSize;
                                merge = groupMerge;
                                break;
                            case 'decimal':
                                if (decimalSize === 'micro') return;
                                size = decimalSize;
                                merge = decimalMerge;
                                break;
                            case 'literal':
                                size = literalSize;
                                break;
                            case 'currency':
                                size = currencySize;
                                break;
                            default:
                                size = 'normal';
                        }
                        return <FixedChar key={index} charSrc={charSrc} char={chunk} size={size} merge={merge} type={chunk.type} />;
                    }
                })}
            </div>
        </div>
    );
};

export default Roller;
